import { useState } from 'react';
import API_ENDPOINTS from '../../config/api';

/**
 * useRAGQuery - Hook for querying RAG service through backend
 *
 * Provides both streaming (new) and non-streaming (legacy fallback) query methods.
 */
const useRAGQuery = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Stream a chat query via SSE. Calls onDelta for each event.
   *
   * @param {string} userQuestion
   * @param {object} context - { instanceId, scenarioId, currentScreen, activeSolution }
   * @param {string} userId
   * @param {string} model - 'sonnet' | 'opus'
   * @param {function} onDelta - ({ type, text?, tool?, summary?, sources?, conversationId? }) => void
   * @returns {Promise<void>}
   */
  const streamQuery = async (userQuestion, context = {}, userId, model = 'sonnet', onDelta) => {
    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        query: userQuestion,
        userId,
        model,
        context: {
          instanceId: context.instanceId,
          scenarioId: context.scenarioId,
          currentScreen: context.currentScreen,
          activeSolution: context.activeSolution || null,
        },
      };

      const response = await fetch(API_ENDPOINTS.RAG_CHAT_STREAM(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Stream failed: ${response.status} ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE lines
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const parsed = JSON.parse(line.slice(6));
            onDelta(parsed);
          } catch {
            // Skip malformed SSE lines
          }
        }
      }

      // Process any remaining buffer
      if (buffer.startsWith('data: ')) {
        try {
          const parsed = JSON.parse(buffer.slice(6));
          onDelta(parsed);
        } catch {
          // Skip
        }
      }
    } catch (err) {
      console.error('❌ Stream error:', err);
      setError(err.message);
      // Don't re-throw — caller already handles via error state.
      // Re-throwing caused unhandled rejections that could crash the React tree.
    } finally {
      setIsLoading(false);
    }
  };

  const loadHistory = async (userId, instanceId = null) => {
    try {
      const url = instanceId
        ? `${API_ENDPOINTS.RAG_CHAT_HISTORY(userId)}?instanceId=${instanceId}`
        : API_ENDPOINTS.RAG_CHAT_HISTORY(userId);

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to load history: ${response.status}`);

      const data = await response.json();
      return { messages: data.messages || [], threads: data.threads || {} };
    } catch (err) {
      console.error('❌ Error loading history:', err);
      return { messages: [], threads: {} };
    }
  };

  const clearConversation = async (userId, instanceId = null) => {
    try {
      const response = await fetch(API_ENDPOINTS.RAG_CHAT_CLEAR(), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, instanceId: instanceId || null }),
      });

      if (!response.ok) throw new Error(`Failed to clear: ${response.status}`);
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('❌ Error clearing conversation:', err);
      return { success: false };
    }
  };

  /** accept / reject / fix on one Kuutio-agent message (reactions v0). Returns the saved row or null. */
  const sendReaction = async ({ messageId, userId, reaction, reason = null }) => {
    try {
      const response = await fetch(API_ENDPOINTS.RAG_CHAT_REACTION(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId, userId, reaction, reason }),
      });
      if (!response.ok) throw new Error(`Failed to save reaction: ${response.status}`);
      return await response.json();
    } catch (err) {
      console.error('❌ Error saving reaction:', err);
      return null;
    }
  };

  return { streamQuery, loadHistory, clearConversation, sendReaction, isLoading, error };
};

export default useRAGQuery;
